
/*
// ██████╗  █████╗ ████████╗ █████╗ 
// ██╔══██╗██╔══██╗╚══██╔══╝██╔══██╗
// ██║  ██║███████║   ██║   ███████║
// ██║  ██║██╔══██║   ██║   ██╔══██║
// ██████╔╝██║  ██║   ██║   ██║  ██║
// ╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚═╝  ╚═╝
*/

import { DepotKey } from "./types2";

export const toSlug = (s: string='', delimiter:string='_') => s.replace(/\s/ig, delimiter).toLowerCase()
export const tuple = <T = string>(...args: T[]) => args;

/*
// ██╗      ██████╗  ██████╗ █████╗ ████████╗██╗ ██████╗ ███╗   ██╗
// ██║     ██╔═══██╗██╔════╝██╔══██╗╚══██╔══╝██║██╔═══██╗████╗  ██║
// ██║     ██║   ██║██║     ███████║   ██║   ██║██║   ██║██╔██╗ ██║
// ██║     ██║   ██║██║     ██╔══██║   ██║   ██║██║   ██║██║╚██╗██║
// ███████╗╚██████╔╝╚██████╗██║  ██║   ██║   ██║╚██████╔╝██║ ╚████║
// ╚══════╝ ╚═════╝  ╚═════╝╚═╝  ╚═╝   ╚═╝   ╚═╝ ╚═════╝ ╚═╝  ╚═══╝
*/

export type MappedKey<T> = keyof T
export type MappedValue<T> = T[keyof T]
export type New_Zealand_State = MappedKey<typeof New_Zealand_States>
export type Australia_State = MappedKey<typeof Australia_States>
export type New_Zealand_StateKey = MappedValue<typeof New_Zealand_States>
export type Australia_StateKey = MappedValue<typeof Australia_States>
export type State = New_Zealand_State | Australia_State
export type StateKey = New_Zealand_StateKey | Australia_StateKey
export const New_Zealand_States = {
    'Auckland':      'nz.auckland',
    'Christchurch':  'nz.christchurch',
    'Queenstown':    'nz.queenstown',
 } as const
export const Australia_States = {
    'Adelaide':      'au.adelaide',
    'Alice Springs': 'au.alice_springs',
    'Airlie Beach':  'au.airlie_beach',
    'Brisbane':      'au.brisbane',
    'Broome':        'au.broome',
    'Byron Bay':     'au.byron_bay',
    'Cairns':        'au.cairns',
    'Darwin':        'au.darwin',
    'Gold Coast':    'au.gold_coast',
    'Hobart':        'au.hobart',
    'Perth':         'au.perth',
    'Melbourne':     'au.melbourne',
    'Sydney':        'au.sydney',
} as const
type CountryCode = "AU" | "NZ"
export type DestinationCurrency = 'AUD'|'NZD'
type LocationData = {
    key: string,
    nice: string,
    state: State,
    country: CountryCode,
}

export const All_States: LocationData[] = Object.entries({
    'NZ': New_Zealand_States,
    'AU': Australia_States,
} as Record<CountryCode, Record<State,StateKey>>).flatMap(([country, states]:[CountryCode, Record<State,StateKey>]) => {
    return Object.entries(states).map(([state,key]:[State,StateKey]) => ({
        // key: toSlug(`${country}.${state}`),
        key,
        nice: `${state}, ${country}`,
        state,
        country,
    }))
}) // [{ key:'au.byron_bay', nice:'Byron Bay, AU', state, country }]


export function isNZ(l: string): l is New_Zealand_State {
    return Object.values(New_Zealand_States).includes(l as any)
}
export function isAustralia(l: string): l is Australia_State {
    return Object.values(Australia_States).includes(l as any)
}
export function isState(l: string): l is State {
    return isNZ(l) || isAustralia(l)
}
export function isLocationKey(l: string): l is State {
    return All_States.some(({ key }) => key == l)
}
export function getLocationByKey(l: string): LocationData {
    return All_States.find(({ key }) => key == l)
}

/*
// ███████╗████████╗ █████╗ ████████╗██╗   ██╗███████╗
// ██╔════╝╚══██╔══╝██╔══██╗╚══██╔══╝██║   ██║██╔════╝
// ███████╗   ██║   ███████║   ██║   ██║   ██║███████╗
// ╚════██║   ██║   ██╔══██║   ██║   ██║   ██║╚════██║
// ███████║   ██║   ██║  ██║   ██║   ╚██████╔╝███████║
// ╚══════╝   ╚═╝   ╚═╝  ╚═╝   ╚═╝    ╚═════╝ ╚══════╝
*/

export const StatusList = {
    'Pending': 'pending',
    'Requote': 'requote',
    'Expired': 'expired',
    'Booked': 'booked',
    'Deleted': 'deleted',
}
export const Statuses = tuple(...Object.values(StatusList))
export const StatusesNice = tuple(...Object.keys(StatusList))
export type Status = typeof Statuses[number]

export function isStatus(s: string): s is Status {
    return Statuses.includes(s as any)
}

export const HR = 60 * 60 * 1000
export const DAY = 24 * HR
export const WEEK = 7 * DAY // 604800000
export const DEFAULT_DISCOUNT_WINDOW = 7 * DAY

export const ORIGIN_MONDAY = 1154872800000 // 1000 weeks before (2025-10-06T00:00:00+10:00 AKA 2025-10-05T14:00:00Z) is 1759672800000-1000*WEEK 
export function getLastMonday(today:Date|number = new Date(), DST=true): Date {
    today = new Date(today)
    const offset = (today.getTime()-ORIGIN_MONDAY) % WEEK
    const monday = new Date(today.getTime()-offset)
    if(DST && isWithinDST(monday)) return ajustToDST(monday) 
    return monday
    // const prevMonday = new Date(today.getTime()+DAY);
    // prevMonday.setDate(today.getDate() - (today.getDay() + 6) % 7);
    // prevMonday.setMilliseconds(0)
    // prevMonday.setSeconds(0)
    // prevMonday.setMinutes(0)
    // prevMonday.setHours(0)
    // return new Date(prevMonday.getTime())
}
export function getTZOffset(date:Date = new Date(), timeZone:string = 'Australia/Sydney') {
    return new Date(date.toLocaleString('en-US', { timeZone })).getTime() - new Date(date.toLocaleString('en-US', { timeZone: 'GMT' })).getTime()
}
export function isWithinDST(date: Date): boolean { // TODO: use env.TZ natively?
    const b=new Date(date.toLocaleString('en-US', { timeZone: 'Australia/Brisbane' })).getTime() // Server handles times in AEST
    const s=new Date(date.toLocaleString('en-US', { timeZone: 'Australia/Sydney' })).getTime() // Sydney can be AEST or AEDT depending on DST
    return Math.round((s-b)/3600e3)!=0
}
function ajustToDST(date:Date):Date {
    return new Date(date.getTime()-HR)
}
export function autoDST(date:Date):Date { // convert AEST date to AEDT if DST applies
    if(isWithinDST(date)) return ajustToDST(date)
    return date
}

export function getNearestMonday(today:Date|number = new Date(), DST=true): Date {
    today = new Date(new Date(today).getTime()+(WEEK/2))
    const offset = (today.getTime()-ORIGIN_MONDAY) % WEEK
    const monday = new Date(today.getTime()-offset)
    if(DST && isWithinDST(monday)) return ajustToDST(monday) 
    return monday
}
export const isSow = (d:Date|number) => {
  const D = new Date(d)
  return isMonday(D) && ((D.getTime()-ORIGIN_MONDAY)%WEEK == 0)
}

export function getMondays(start = new Date(), end = new Date(Date.now() + 7 * DAY)) {
    const mondays = [];
    const date = getLastMonday(start);
    while (date.getTime() < end.getTime() && mondays.length < 1000) {
        mondays.push(new Date(date));
        date.setTime(date.getTime() + WEEK)
    }
    return mondays
}
export const getQuarters = (start = 2025, end = new Date().getFullYear() + 1) =>
    Array.from({ length: (end - start + 1) * 4 }, (_, i) => {
        const year = start + Math.floor(i / 4);
        const quarter = (i % 4) + 1;
        return `${year}-Q${quarter}`;
    });
export const getCurrentQuarter = () => {
    const now = new Date();
    const year = now.getFullYear();
    const quarter = Math.floor(now.getMonth() / 3) + 1;
    return `${year}-Q${quarter}`;
};

export const quarterToDates = (q = getCurrentQuarter()) => {
    const [year, qNum] = q?.split('-Q') ?? [];
    const month = (parseInt(qNum) - 1) * 3;
    return [
        new Date(parseInt(year), month, 1),
        new Date(parseInt(year), month + 3, 0)
    ].map(d => getNearestMonday(d));
};

export const rangeQuarter = (q: string) => {
    const [start, end] = quarterToDates(q)
    return getMondays(start, end).map(d => d.getTime())
}

export function isMonday(d: Date) {
    return (d.getTime()-ORIGIN_MONDAY)%WEEK <= DAY
}
export function isPastOrNextMonday(d: Date) {
    return d.getTime() <= (getLastMonday().getTime() + WEEK + 3*DAY)
}

export const startOfDay = (t: Date): Date => {
    const T = new Date(t)
    const offset = (T.getTime()-ORIGIN_MONDAY) % DAY
    return new Date(T.getTime()-offset)
}
export const endOfDay = (t: Date): Date => {
    return new Date(startOfDay(t).getTime()+DAY-1)
}

/*
//  ██████╗ ██████╗ ████████╗██╗ ██████╗ ███╗   ██╗███████╗
// ██╔═══██╗██╔══██╗╚══██╔══╝██║██╔═══██╗████╗  ██║██╔════╝
// ██║   ██║██████╔╝   ██║   ██║██║   ██║██╔██╗ ██║███████╗
// ██║   ██║██╔═══╝    ██║   ██║██║   ██║██║╚██╗██║╚════██║
// ╚██████╔╝██║        ██║   ██║╚██████╔╝██║ ╚████║███████║
//  ╚═════╝ ╚═╝        ╚═╝   ╚═╝ ╚═════╝ ╚═╝  ╚═══╝╚══════╝
*/

// Amenity

export const NoToiletShower = `No Toilet & Shower`
export const ToiletNoShower = `Toilet* & No Shower`
export const ToiletShower = `Toilet & Shower`
export const AmenityRequested = `Requested with company`
export const Amenity = [
    NoToiletShower,
    ToiletNoShower,
    ToiletShower,
    AmenityRequested,
] as const
export type AmenityOption = typeof Amenity[number]

// Fridge

export const Fridge = `Fridge`
export const NoFridge = `No fridge`
export const FridgeOptions = [
    Fridge,
    NoFridge,
] as const
export type FridgeOption = typeof FridgeOptions[number]

// Transmission

export const Automatic = 'Automatic'
export const Manual = 'Manual'
export const TransmissionOptions = [
    Automatic,
    Manual,
] as const
export type TransmissionOption = typeof TransmissionOptions[number]

// SpecOptions


export const SpecOptionsList = {
    'Auto': 'auto',
    'Toilet & Shower': 'toilet_shower',
    'Fridge': 'fridge',
    'Anchor Points: 1': 'anchor_1',
    'Anchor Points: 2': 'anchor_2',
}
export const SpecOptionsNice = tuple(...Object.keys(SpecOptionsList))
export const SpecOptionsSlug = tuple(...Object.values(SpecOptionsList))
export type SpecOptionNice = typeof SpecOptionsNice[number]
export type SpecOptionSlug = typeof SpecOptionsSlug[number]
export const optionList = Object.entries(SpecOptionsList).map(([nice, slug]) => ({ slug, nice }))

/*
anchor_points:_1: true
anchor_points:_2: true
auto: true
fridge: true
toilet_&_shower: true
*/

/*
// ████████╗██╗   ██╗██████╗ ███████╗███████╗
// ╚══██╔══╝╚██╗ ██╔╝██╔══██╗██╔════╝██╔════╝
//    ██║    ╚████╔╝ ██████╔╝█████╗  ███████╗
//    ██║     ╚██╔╝  ██╔═══╝ ██╔══╝  ╚════██║
//    ██║      ██║   ██║     ███████╗███████║
//    ╚═╝      ╚═╝   ╚═╝     ╚══════╝╚══════╝
*/
export const VehicleTypeList = {
    Sleeper: 'sleeper',
    "Low Top": 'low_top',
    "Hi Top": 'hi_top',
    Motorhome: 'motorhome',
    '4x4': '4x4',
}


export const VehicleTypes = tuple(...Object.values(VehicleTypeList))
export const VehicleTypesNice = tuple(...Object.keys(VehicleTypeList))
//     'Sleeper', // Sleep
//     'Low Top', // LT
//     'Hi Top', // HT
//     'Motorhome', // MH
//     '4x4'      , // 4x4
// )
export type VehicleType = typeof VehicleTypes[number]
export type VehicleTypeNice = typeof VehicleTypesNice[number]
export const typeList = VehicleTypesNice.map(vehicleType => ({
    slug: toSlug(vehicleType),
    nice: vehicleType,
}))

export const BedTypeList = {
    '1 Bed': 1,
    '1 Bed + Tent': 1.5,
    '2 Beds': 2,
    '2 Beds + Tent': 2.5,
    '3 Beds': 3,
    '4 Beds': 4,
} as const
export const BedTypesNumeric = Object.values(BedTypeList) as BedTypeNumeric[]
export const BedTypes = Object.keys(BedTypeList) as BedType[]
export type BedTypeNumeric = typeof BedTypeList[BedType]
export type BedType = keyof typeof BedTypeList

export const NEWEST_MODEL = `Newest Model`
export const NEWER_MODEL = 'Newer Model'
export const BUDGET_MODEL = `Budget Model`
export const OLDER_MODEL = `Older Model`
export const OLD_APOLLO_MODEL = `Old Apollo Model`
export const OLD_CHEAPA_MODEL = `Old Cheapa Model`
export const OLD_BRITZ_MODEL = `Old Britz Model`
export const OLD_MAUI_MODEL = `Old Maui Model`
export const NewnessOptions = [
    NEWEST_MODEL,
    NEWER_MODEL,
    BUDGET_MODEL,
    OLDER_MODEL,
    OLD_APOLLO_MODEL,
    OLD_CHEAPA_MODEL,
    OLD_BRITZ_MODEL,
    OLD_MAUI_MODEL,
] as const
export type NewnessOption = typeof NewnessOptions[number]

/*
//  ██████╗ ██████╗ ███████╗██████╗  █████╗ ████████╗ ██████╗ ██████╗ ███████╗
// ██╔═══██╗██╔══██╗██╔════╝██╔══██╗██╔══██╗╚══██╔══╝██╔═══██╗██╔══██╗██╔════╝
// ██║   ██║██████╔╝█████╗  ██████╔╝███████║   ██║   ██║   ██║██████╔╝███████╗
// ██║   ██║██╔═══╝ ██╔══╝  ██╔══██╗██╔══██║   ██║   ██║   ██║██╔══██╗╚════██║
// ╚██████╔╝██║     ███████╗██║  ██║██║  ██║   ██║   ╚██████╔╝██║  ██║███████║
//  ╚═════╝ ╚═╝     ╚══════╝╚═╝  ╚═╝╚═╝  ╚═╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝╚══════╝
*/

// import { australia, new_zealand } from "./data";
// const prefix_key = (pre = 'au.') => ([k, v]) => [`${pre}${k}`, v.name]
export const OperatorList = { // Object.fromEntries([ // TODO compute this automatically from data.ts
    // ...Object.entries(australia).map(prefix_key('au.')),
    'au.apollo': 'Apollo',
    'au.britz': 'Britz',
    'au.maui': 'Maui',
    'au.mighty': 'Mighty',
    'au.camperman': `Camperman`,
    'au.cheapa': `Cheapa`,
    'au.hippie': `Hippie`,
    'au.jucy': `Jucy`,
    'au.letsgo': `Lets Go`,
    'au.spaceships': `Spaceships`,
    'au.star-rv': `Star RV`,
    'au.travellers-autobarn': 'Travellers Autobarn',
    // 'au.hqr': 'HQ Rentals',
    // ...Object.entries(new_zealand).map(prefix_key('nz.')),
    'nz.apollo': `Apollo`,
    'nz.britz': `Britz`,
    'nz.maui': `Maui`,
    'nz.mighty': `Mighty`,
    'nz.cheapa': `Cheapa`,
    'nz.hippie': `Hippie`,
    'nz.jucy': `Jucy`,
    'nz.spaceships': `Spaceships`,
    'nz.star-rv': `Star RV`,
    'nz.travellers-autobarn': `Travellers Autobarn`,
    'nz.barefoot-campers': `Barefoot Campers`,
    'nz.biglittle': `Big Little`,
    // 'nz.hqr': 'HQ Rentals',
    'nz.lylo': 'Lylo',
    'nz.escape': 'Escape',
    'nz.lucky': 'Lucky',
    'au.campervan-village': `Campervan Village`,
    'nz.campervan-village': `Campervan Village`,
    'nz.happy': `Happy`,
    'au.awesome': `Awesome`,
    'nz.euro': `Euro Campers`,
    'nz.mad': `Mad Campers`,
    // 'nz.mighty-lite': `MightyLITE`,
    // 'au.mighty-lite': `MightyLITE`,
// ]) 
} as const

export const rp_location = { // TODO: use All_States
    // AU
    'Adelaide': 'au.adelaide',
    'Alice Springs': 'au.alice_springs',
    // '' : 'au.airlie_beach',
    'Brisbane': 'au.brisbane',
    'Broome': 'au.broome',
    'Cairns': 'au.cairns',
    'Darwin': 'au.darwin',
    'Gold Coast': 'au.gold_coast',
    'Hobart': 'au.hobart',
    'Perth': 'au.perth',
    'Melbourne': 'au.melbourne',
    'Sydney': 'au.sydney',
    // NZ
    'Auckland': 'nz.auckland',
    'Christchurch': 'nz.christchurch',
    'Queenstown': 'nz.queenstown',
} as const

export const OperatorRawKeys = [
    'apollo',
    'awesome',
    'britz',
    'maui',
    'mighty',
    'camperman',
    'cheapa',
    'hippie',
    'jucy',
    'letsgo',
    'spaceships',
    'star-rv',
    'travellers-autobarn',
    'barefoot-campers',
    'biglittle',
    // 'hqr',
    'generic',
    'lylo',
    'escape',
    'lucky',
    'campervan-village',
    'happy',
    'mighty-lite',
] as const

export const locationIDList = ({
    "thl_cosmos": {
        "hbt": "au.hobart",
        "adl": "au.adelaide",
        "bne": "au.brisbane",
        "asp": "au.alice_springs",
        "syd": "au.sydney",
        "mel": "au.melbourne",
        "cns": "au.cairns",
        "bme": "au.broome",
        "per": "au.perth",
        "drw": "au.darwin",
        "zqn": "nz.queenstown",
        "chc": "nz.christchurch", // airport
        "akl": "nz.auckland", // airport
    },
    "nz.happy": {}, // No API yet
    "nz.mighty-lite": {}, // No API yet
    "au.apollo": {
        // not on API
    },
    "au.awesome": {
        // not on API
    },
    "au.britz": {
        // not on API
    },
    "au.camperman": {
        "1": "au.cairns",
        "2": "au.brisbane",
        "4": "au.airlie_beach",
        "5": "au.sydney",
        "6": "au.melbourne",
    },
    "au.campervan-village": {
        "1": "au.sydney",
        "2": "au.melbourne",
        "3": "au.brisbane",
        "7": "au.cairns",
        "8": "au.adelaide",
        "9": "au.alice_springs",
        "10": "au.darwin",
        "11": "au.broome",
        "12": "au.perth",
        "22": "au.airlie_beach",
        "25": "au.hobart",
        "26": "au.byron_bay",
        // "27": "au.exmouth",
    },
    "au.mighty-lite": {
        // not on API
    },
    "au.cheapa": {
        // not on API
    },
    "au.hippie": {
        // not on API
    },
    "au.jucy": {
        // not on API
    },
    "au.letsgo": {
        "1": "au.melbourne",
        "2": "au.sydney",
        "3": "au.brisbane",
        "4": "au.cairns",
        "5": "au.adelaide",
        "6": "au.perth",
        "8": "au.darwin",
        "9": "au.broome",
        // "11": "au.coffsharbour",
        // "23": "au.geelong",
        // "24": "au.canberra",
        // "28": "au.townsville",
        "29": "au.hobart",
    },
    "au.maui": {
        // not on API
    },
    "au.mighty": {
        // not on API
    },
    "au.spaceships": {
        "1": "au.sydney",
        "6": "au.brisbane",
        "7": "au.cairns",
        "8": "au.melbourne",
        "9": "au.adelaide",
        "60": "au.airlie_beach",
        "62": "au.hobart",
    },
    "au.star-rv": {
        // not on API
    },
    "au.travellers-autobarn": {
        "7": "au.brisbane",
        "8": "au.melbourne",
        "9": "au.perth",
        "10": "au.darwin",
        "11": "au.cairns",
        "12": "au.sydney",
    },
    "nz.apollo": {
        // not on API
    },
    "nz.barefoot-campers": {
        "1": "nz.auckland",
        "2": "nz.christchurch",
        // "3": "nz.wellington",
        "5": "nz.queenstown",
        // "9": "nz.picton",
        // "15": "nz.aucklandcity",
    },
    "nz.biglittle": {
        // TODO: fix locations format on operator-config
        // 4: "nz.albany_storage", // ?
        // 5: "nz.in_spray", // ?
        // 3: "nz.vanlifer", // ?
        1: "nz.auckland",
        7: "nz.queenstown",
        2: "nz.christchurch",
    },
    "nz.britz": {
        // not on API
    },
    "nz.campervan-village": {
        "16": "nz.auckland",
        "15": "nz.christchurch",
        "22": "nz.queenstown"
    },
    "nz.cheapa": {
        // not on API
    },
    "nz.escape": {
        1: 'nz.auckland',
        // 2: 'nz.wellington',
        3: 'nz.christchurch',
        // 6: 'nz.hamilton',
        7: 'nz.queenstown',
    },
    "nz.hippie": {
        // not on API
    },
    "nz.jucy": {
        // not on API
    },
    "nz.lucky": {
        2: 'nz.auckland',
        1: 'nz.christchurch',
    },
    'nz.lylo': {
        "1": "nz.auckland",
        "7": "nz.christchurch",
    },
    "nz.maui": {
        // not on API
    },
    "nz.mighty": {
        // not on API
    },
    "nz.spaceships": {
        "1": "nz.auckland",
        "2": "nz.christchurch",
    },
    "nz.star-rv": {
        // not on API
    },
    "nz.travellers-autobarn": {
        "1": "nz.auckland",
        "2": "nz.christchurch",
        // "9": "nz.hamilton",
        // "13": "fleet", // internal?
    },
    "nz.euro": {
        // not on API
    },
    "nz.mad": {
        // not on API
    },
} as Record<Operator, Record<any, DepotKey>> & { "thl_cosmos": Record<any, DepotKey> })

export const AllOperators = Object.fromEntries(Object.entries(OperatorList).map(([k,v])=>[k.split('.')[1], v])) as Record<typeof OperatorRawKeys[number], typeof OperatorList[keyof typeof OperatorList]>
export function NormalizeToCompanyID(Company: string | OperatorKey, Country: string) {
    if(!Company) return ''
    const [prefix,suffix] = Company?.split('.')
    // const out = Object.entries(OperatorList).find(([k,v])=>k.split('.')[0]==NormalizeToCountryCode(Country)?.toLowerCase() && v == Company)?.[0] ?? Company
    const remap = { // In case values are not normalized properly, they will still display consistently:
        'Star RV': 'star-rv',
        'Barefoot Campers': 'barefoot-campers',
        'Lets Go': 'letsgo',
        'Big Little Campers NZ': 'biglittle',
        'Apollo': 'apollo',
    } as Record<string, keyof typeof AllOperators>
    const out = remap[Company] ?? Object.entries(AllOperators).find((kv) => kv.includes(suffix??Company))?.[0] ?? Company
    return out
}
export function NormalizeToCompanyName(Company: string | OperatorKey, Country: string) {
    return AllOperators[NormalizeToCompanyID(Company,Country)];
}

export const OperatorsGeneric = Object.fromEntries(Object.entries(OperatorList).map(([k, v]) => [k.split('.')[1], v])) // TODO move to types and name properly
export const Operators = Object.keys(OperatorList) as Array<keyof typeof OperatorList>
export type OperatorKey = typeof OperatorRawKeys[number]
export type Operator = typeof Operators[number]
export const OperatorNames = tuple(...Object.values(OperatorList))
export type OperatorName = typeof Operators[number]

export function getOperatorByKey(l: string): { nice: string, slug: string, region: string } {
    let [region, slug] = [...l.split('.'), null]
    if (!region) return null
    if (!slug) {
        slug = region
        region = null
    }
    const nice = Object.entries(OperatorList).find(([k]) => k == l || k == slug)?.[1]
    return {
        nice,
        slug,
        region,
    }
}

// export const multipliers:Record<keyof typeof OperatorList,number> = { // Not to be confused with netFactor
// }

/*
//  █████╗ ██████╗ ██████╗ 
// ██╔══██╗██╔══██╗██╔══██╗
// ███████║██████╔╝██████╔╝
// ██╔══██║██╔═══╝ ██╔═══╝ 
// ██║  ██║██║     ██║     
// ╚═╝  ╚═╝╚═╝     ╚═╝     
*/

export type WeekStart = number
export type AgentEmail = string
export type TeamName = string
export type Segment = { value:number, color?:string, label?:number|string }
export type PaymentStats = {
  [k: string]: Record<AgentEmail|TeamName, number|Segment[]>
}
export type RatesResponse = {
    rates: Record<string, number>,
}
export type PaymentResponse = {
    payments: Array<Payment>,
    rates: RatesResponse,
    paymentStats: PaymentStats,
    target: number,
    youManage: string[],
    allAgentsAndAdmins: Record<string,string>, // email:name
    allTeams: Record<string, string[]>, // email:team[]
}

export const YYYYMMDD = (d:Date|string|number = Date.now()) => { // new Intl.DateTimeFormat('en-CA', { dateStyle: 'short', timeZone: 'Australia/Sydney' }).format(new Date(d))
    const D = new Date(d)
    const year = D.getFullYear();
    const month = String(D.getMonth() + 1).padStart(2, '0'); // Add 1 because 0-based months
    const day = String(D.getDate()).padStart(2, '0');
    return [year,month,day].join('-')
}
export const YYYYMMDD_AEDT = (d:Date|string|number = Date.now()) => {
    const D = new Date(d)
    return D.toLocaleDateString('en-GB', { timeZone: "Australia/Sydney" }).split('/').reverse().join('-')
}
export const JucyDate = (d:Date|string|number = Date.now()) => new Intl.DateTimeFormat('en-CA', { dateStyle: 'short', timeZone: 'Australia/Sydney' }).format(new Date(d))

// const YYYYMMDD = (d:Date|string|number) => {
//     const D = new Date(d)
//     const lpad = (v: any,l=2,c=0,s=v=>`${v}`) => (s(c).repeat(l)+s(v)).slice(-Math.max(s(v).length, l))
//     return [D.getFullYear(), lpad(D.getMonth()), lpad(D.getDate())].join('-')
// }
export const DDMMYYYY = (d: Date) => d.toLocaleDateString('en-GB', { timeZone: "Australia/Sydney" })

export const resJson:((r:Response,ctx?:any)=>Promise<any>)&{ctx:(ctx?:any)=>(r:Response)=>Promise<any>} = Object.assign(async (res:Response, ctx?:any) => {
    const txt = await res.text()
    try {
        return JSON.parse(txt)
    } catch (e) {
        console.error(`FAILED TO PARSE RESPONSE JSON: ${txt} :`, e?.toString() ?? 'unknown error')
        if(ctx) console.error(`EXTRA CONTEXT: ${JSON.stringify(ctx, null, 4)}`)
    }
    return {}
}, {ctx(ctx:any){return async (res:Response) => this(res,ctx)}})

export type Product = {
    id: number | string | null, // used by upstream APIs to identify products
    rp_id: string, // used in ZD field 6684723339161 to select product used in finalisation
    name: string,
    type: VehicleType,
    pax: number,
    bed: BedType,
    transmission: TransmissionOption,
    fridge: FridgeOption,
    amenity: AmenityOption,
    newness: NewnessOption,
    specs: string,
    boosterSeats: number,
    imagesLink: string,
    youtubeLink: string,
    internalDataLink: string,
}
export type InsurancePolicyOption = {
    id: number|string,
    name: string,
    fees: number, // daily
    totalinsuranceamount: number,
    maximumprice: number,
    minimumprice?: number,
    excessamount: number, // bond
}
export type OptionOffer = {
    provider_key: ProviderKey,
    fee: number,
    daily: number,
    total: number,
    policies?: Array<InsurancePolicyOption>,
    id?: string|number, // from upstream API
    rp_id?: string, // from ZD
}
export type Option = Product & OptionOffer & {
    discount_total: number,
    override_net?: number, // cents total
    daily_for_net?: number, // cents daily
}
export type ProviderKey = Operator
export type ProviderName = OperatorName

export const sleep = (ms=1000) => new Promise(a=>setTimeout(a,ms))
export const ifNaN = (v=1, d=null) => isNaN(v) ? d : v

// ProviderOptions for S10 server side payment profiles

export type PaymentType = 'FULL_PAYMENT' | 'DEPOSIT'
export type CommissionMode = `NET_UP` | `GROSS_DOWN`
export type PaymentProfile = {
    dailyCommission: number,
    insuranceCommission?:number,
    extraCommission?:number,
    mode: CommissionMode,
    type: PaymentType,
}
export type DATA_SOURCE = (q: QueryConfiguration & { products: Product[] }, artifact?: Artifact) => Option[] | Promise<Option[]>

// For the ability to use the API directly to list products, in addition to being a DATA_SOURCE
export type ListAllResponse = Promise<{available:Option[],req:any,res:any}>
export type SDK = {
    listAll: (pickup:Date, dropoff:Date, from:RPLocation, to:RPLocation, provider_key:Operator) => ListAllResponse,
    locations: () => any,
    config: {
        locations: Partial<Record<RPLocation, number|string>>,
        ageid: number|string,
        multiplier: number,
    },
}

export type ProviderOptions = {
    API: DATA_SOURCE & SDK,
    profile: PaymentProfile,
    stripeProductId: string,
    // locations: Record<string, number>, // Map location slugs (au.gold_coast) to specific ID for the provider (1)
}

export const australia_locations = [
    'au.alice_springs',
    'au.brisbane',
    'au.broome',
    'au.melbourne',
    'au.perth',
    'au.darwin',
    'au.cairns',
    'au.sydney',
    'au.airlie_beach',
    'au.gold_coast',
    'au.hobart',
    'au.adelaide',
] as const
export const new_zealand_locations = [
    'nz.auckland',
    'nz.christchurch',
    'nz.queenstown',
] as const

export type RPLocationAU = typeof australia_locations[number] // 'au.alice_springs' | 'au.brisbane' | 'au.melbourne' | 'au.perth' | 'au.darwin' | 'au.cairns' | 'au.sydney' | `au.airlie_beach` | `au.gold_coast` | 'au.hobart' | 'au.adelaide'
// Alice Springs	ASP
// Broome	BME
// 'au.melbourne_airport': 'MAP', // Melbourne Airport / Australia
export type RPLocationNZ = typeof new_zealand_locations[number] // "nz.auckland" | "nz.christchurch" | "nz.queenstown"
export type RPLocation =  RPLocationAU | RPLocationNZ

// export const Operators = [ // use OperatorName
//     'apollo',
//     'barefoot-campers',
//     'biglittle',
//     'britz',
//     'camperman',
//     'cheapa',
//     'generic',
//     'hippie',
//     'jucy',
//     'letsgo',
//     'maui',
//     'mighty',
//     'spaceships',
//     'star-rv',
//     'travellers-autobarn',
// ] as const
type OperatorSlug = typeof Operators[number]

export type QueryConfiguration = {
    pickup:RPLocation,
    dropoff:RPLocation,
    pickup_date: Date,
    days: number,
    dropoff_date: Date,
    specs?,
    types,
    pax: number,
    discount_percent: number,
    provider_key: ProviderKey,
    insurance?: boolean,
    license_origin?: License_Origin,
}

export type AgentInfo = {
    id:number,
    email: string,
    name: string,
    avatar: string,
}
export type res_quote = {
    id:number,
    created_at: string,
    key: string,
    prepared_for: string,
    name: string,
    surname: string,
    title: string,
    reference: string,
    offer_duration: number,
    pax: number,
    operators: string[],
    options: any[],
    types: any[],
    discount_percent: number,
    discount_expiry: string,
    pickup: RPLocation,
    dropoff: RPLocation,
    pickup_date: string,
    dropoff_date: string,
    status: string,
    offers: Array<Option>,
    selection: any|null,
    selection_made:	any|null,
    by: AgentInfo,
    url: string,
}

export type res_error = {
    error: string,
    message?: string,
    location?: string,
    artifact?: string,
}
export type res_finalise_quote = {
    quote:res_quote,
    profiles:Record<string,PaymentProfile>
}

// For use with artifacts
export type Artifact = any
export const log = (a:Artifact={},...v:any[]) => {
    console.log(...v);
    a.log = [...(a?.log??[]), v]
    a.start = a.start ?? Date.now()
}

export type SaleType = 'NewSaleDeposit' | 'NewSaleFullSplit' | 'NewSaleFullFull' | 'PayOffFull' |
    'MANUAL-NS' | 'MANUAL-PO' | 'MANUAL-REF' | 
    'WISE-NS' | 'WISE-PO' | 'WISE-REF' |
    'VIRTUAL' // 'MANUAL' | 'MANUAL-WISE' | 'Refund'

export const SaleTypeCounted:Partial<Record<SaleType,boolean>> = { // For units and duration stats
    'NewSaleDeposit': true,
    'NewSaleFullSplit': true,
    'NewSaleFullFull': true,
    'MANUAL-NS': true,
    'WISE-NS': true,
}
export const SaleTypeContributesToStripeTotal:Partial<Record<SaleType,-1|0|1>> = { // For totals. -1 indicates subtract, but REF usually have negative value
    'NewSaleDeposit': 1,
    'NewSaleFullSplit': 1,
    'NewSaleFullFull': 1,
    'PayOffFull': 1,
    'MANUAL-NS': 1,
    'MANUAL-PO': 1,
    'MANUAL-REF': 1,
}
export const SaleTypeContributesToWiseTotal:Partial<Record<SaleType,-1|0|1>> = { // For totals. -1 indicates subtract, but REF usually have negative value
    'WISE-NS': 1,
    'WISE-PO': 1,
    'WISE-REF': 1,
}

export type Payment = {
    id: number, // increment
    // stripe_payment: string, // pay_1234
    // stripe_invoice: string, // in_1234
    created_at: Date, // created
    updatedAt: Date, // updated
    paid_at: Date,

    // PAX
    PaxName: string,
    PaxLocation: string,
    ZDTicketID?: string,
    // DateForGraphs
    StripeInvoiceID?: string,
    StripePaymentID?: string,

    // BOOKING
    Country: string, // Where the trip will take place
    Company: OperatorKey,
    Model: string,
    Duration: number,

    // ACCOUNTS
    SaleType: SaleType,
    StripeAUD?: number, // 
    StripeNZD?: number, // 
    CardFee: number, // (2.9%)
    StripeFee: number,
    TotalAUD: number,

    // Currency: string, // NOTE: this is not the currency of the payment, but the currency of the trip location

    // COMMISION
    CamperAUD?: number,
    CamperNZD?: number,

    // AGENT
    TeamMember: string,
    AdReferral: string,
    InitialsConfirmation: string,
    CommAUD: number,
    NOTES: string,

    meta_net: number,
    meta_created: string,
    meta_comm: number,
    StripeCustomerID: string,
    StripeChargeID: string,
    StripeTransactionID: string,
    StripePayoutID: string,
    StripePayoutAmount: number,
    StripePayoutDate: Date,
}

export const AllCountries = [ 
    {"name": "Afghanistan", "code": "AF"}, 
    {"name": "land Islands", "code": "AX"}, 
    {"name": "Albania", "code": "AL"}, 
    {"name": "Algeria", "code": "DZ"}, 
    {"name": "American Samoa", "code": "AS"}, 
    {"name": "AndorrA", "code": "AD"}, 
    {"name": "Angola", "code": "AO"}, 
    {"name": "Anguilla", "code": "AI"}, 
    {"name": "Antarctica", "code": "AQ"}, 
    {"name": "Antigua and Barbuda", "code": "AG"}, 
    {"name": "Argentina", "code": "AR"}, 
    {"name": "Armenia", "code": "AM"}, 
    {"name": "Aruba", "code": "AW"}, 
    {"name": "Australia", "code": "AU"},  //
    {"name": "Austria", "code": "AT"}, 
    {"name": "Azerbaijan", "code": "AZ"}, 
    {"name": "Bahamas", "code": "BS"}, 
    {"name": "Bahrain", "code": "BH"}, 
    {"name": "Bangladesh", "code": "BD"}, 
    {"name": "Barbados", "code": "BB"}, 
    {"name": "Belarus", "code": "BY"}, 
    {"name": "Belgium", "code": "BE"}, 
    {"name": "Belize", "code": "BZ"}, 
    {"name": "Benin", "code": "BJ"}, 
    {"name": "Bermuda", "code": "BM"}, 
    {"name": "Bhutan", "code": "BT"}, 
    {"name": "Bolivia", "code": "BO"}, 
    {"name": "Bosnia and Herzegovina", "code": "BA"}, 
    {"name": "Botswana", "code": "BW"}, 
    {"name": "Bouvet Island", "code": "BV"}, 
    {"name": "Brazil", "code": "BR"}, 
    {"name": "British Indian Ocean Territory", "code": "IO"}, 
    {"name": "Brunei Darussalam", "code": "BN"}, 
    {"name": "Bulgaria", "code": "BG"}, 
    {"name": "Burkina Faso", "code": "BF"}, 
    {"name": "Burundi", "code": "BI"}, 
    {"name": "Cambodia", "code": "KH"}, 
    {"name": "Cameroon", "code": "CM"}, 
    {"name": "Canada", "code": "CA"}, 
    {"name": "Cape Verde", "code": "CV"}, 
    {"name": "Cayman Islands", "code": "KY"}, 
    {"name": "Central African Republic", "code": "CF"}, 
    {"name": "Chad", "code": "TD"}, 
    {"name": "Chile", "code": "CL"}, 
    {"name": "China", "code": "CN"}, 
    {"name": "Christmas Island", "code": "CX"}, 
    {"name": "Cocos (Keeling) Islands", "code": "CC"}, 
    {"name": "Colombia", "code": "CO"}, 
    {"name": "Comoros", "code": "KM"}, 
    {"name": "Congo", "code": "CG"}, 
    {"name": "Congo, The Democratic Republic of the", "code": "CD"}, 
    {"name": "Cook Islands", "code": "CK"}, 
    {"name": "Costa Rica", "code": "CR"}, 
    {"name": "Cote D\"Ivoire", "code": "CI"}, 
    {"name": "Croatia", "code": "HR"}, 
    {"name": "Cuba", "code": "CU"}, 
    {"name": "Cyprus", "code": "CY"}, 
    {"name": "Czech Republic", "code": "CZ"}, 
    {"name": "Denmark", "code": "DK"}, 
    {"name": "Djibouti", "code": "DJ"}, 
    {"name": "Dominica", "code": "DM"}, 
    {"name": "Dominican Republic", "code": "DO"}, 
    {"name": "Ecuador", "code": "EC"}, 
    {"name": "Egypt", "code": "EG"}, 
    {"name": "El Salvador", "code": "SV"}, 
    {"name": "Equatorial Guinea", "code": "GQ"}, 
    {"name": "Eritrea", "code": "ER"}, 
    {"name": "Estonia", "code": "EE"}, 
    {"name": "Ethiopia", "code": "ET"}, 
    {"name": "Falkland Islands (Malvinas)", "code": "FK"}, 
    {"name": "Faroe Islands", "code": "FO"}, 
    {"name": "Fiji", "code": "FJ"}, 
    {"name": "Finland", "code": "FI"}, 
    {"name": "France", "code": "FR"}, 
    {"name": "French Guiana", "code": "GF"}, 
    {"name": "French Polynesia", "code": "PF"}, 
    {"name": "French Southern Territories", "code": "TF"}, 
    {"name": "Gabon", "code": "GA"}, 
    {"name": "Gambia", "code": "GM"}, 
    {"name": "Georgia", "code": "GE"}, 
    {"name": "Germany", "code": "DE"}, 
    {"name": "Ghana", "code": "GH"}, 
    {"name": "Gibraltar", "code": "GI"}, 
    {"name": "Greece", "code": "GR"}, 
    {"name": "Greenland", "code": "GL"}, 
    {"name": "Grenada", "code": "GD"}, 
    {"name": "Guadeloupe", "code": "GP"}, 
    {"name": "Guam", "code": "GU"}, 
    {"name": "Guatemala", "code": "GT"}, 
    {"name": "UK", "code": "GG"}, 
    {"name": "Guinea", "code": "GN"}, 
    {"name": "Guinea-Bissau", "code": "GW"}, 
    {"name": "Guyana", "code": "GY"}, 
    {"name": "Haiti", "code": "HT"}, 
    {"name": "Heard Island and Mcdonald Islands", "code": "HM"}, 
    {"name": "Holy See (Vatican City State)", "code": "VA"}, 
    {"name": "Honduras", "code": "HN"}, 
    {"name": "Hong Kong", "code": "HK"}, 
    {"name": "Hungary", "code": "HU"}, 
    {"name": "Iceland", "code": "IS"}, 
    {"name": "India", "code": "IN"}, 
    {"name": "Indonesia", "code": "ID"}, 
    {"name": "Iran, Islamic Republic Of", "code": "IR"}, 
    {"name": "Iraq", "code": "IQ"}, 
    {"name": "Ireland", "code": "IE"}, 
    {"name": "Isle of Man", "code": "IM"}, 
    {"name": "Israel", "code": "IL"}, 
    {"name": "Italy", "code": "IT"}, 
    {"name": "Jamaica", "code": "JM"}, 
    {"name": "Japan", "code": "JP"}, 
    {"name": "Jersey", "code": "JE"}, 
    {"name": "Jordan", "code": "JO"}, 
    {"name": "Kazakhstan", "code": "KZ"}, 
    {"name": "Kenya", "code": "KE"}, 
    {"name": "Kiribati", "code": "KI"}, 
    {"name": "Korea, Democratic People\"S Republic of", "code": "KP"}, 
    {"name": "Korea, Republic of", "code": "KR"}, 
    {"name": "Kuwait", "code": "KW"}, 
    {"name": "Kyrgyzstan", "code": "KG"}, 
    {"name": "Lao People\"S Democratic Republic", "code": "LA"}, 
    {"name": "Latvia", "code": "LV"}, 
    {"name": "Lebanon", "code": "LB"}, 
    {"name": "Lesotho", "code": "LS"}, 
    {"name": "Liberia", "code": "LR"}, 
    {"name": "Libyan Arab Jamahiriya", "code": "LY"}, 
    {"name": "Liechtenstein", "code": "LI"}, 
    {"name": "Lithuania", "code": "LT"}, 
    {"name": "Luxembourg", "code": "LU"}, 
    {"name": "Macao", "code": "MO"}, 
    {"name": "Macedonia, The Former Yugoslav Republic of", "code": "MK"}, 
    {"name": "Madagascar", "code": "MG"}, 
    {"name": "Malawi", "code": "MW"}, 
    {"name": "Malaysia", "code": "MY"}, 
    {"name": "Maldives", "code": "MV"}, 
    {"name": "Mali", "code": "ML"}, 
    {"name": "Malta", "code": "MT"}, 
    {"name": "Marshall Islands", "code": "MH"}, 
    {"name": "Martinique", "code": "MQ"}, 
    {"name": "Mauritania", "code": "MR"}, 
    {"name": "Mauritius", "code": "MU"}, 
    {"name": "Mayotte", "code": "YT"}, 
    {"name": "Mexico", "code": "MX"}, 
    {"name": "Micronesia, Federated States of", "code": "FM"}, 
    {"name": "Moldova, Republic of", "code": "MD"}, 
    {"name": "Monaco", "code": "MC"}, 
    {"name": "Mongolia", "code": "MN"}, 
    {"name": "Montenegro", "code": "ME"},
    {"name": "Montserrat", "code": "MS"},
    {"name": "Morocco", "code": "MA"}, 
    {"name": "Mozambique", "code": "MZ"}, 
    {"name": "Myanmar", "code": "MM"}, 
    {"name": "Namibia", "code": "NA"}, 
    {"name": "Nauru", "code": "NR"}, 
    {"name": "Nepal", "code": "NP"}, 
    {"name": "Netherlands", "code": "NL"}, 
    {"name": "Netherlands Antilles", "code": "AN"}, 
    {"name": "New Caledonia", "code": "NC"}, 
    {"name": "New Zealand", "code": "NZ"}, //
    {"name": "Nicaragua", "code": "NI"}, 
    {"name": "Niger", "code": "NE"}, 
    {"name": "Nigeria", "code": "NG"}, 
    {"name": "Niue", "code": "NU"}, 
    {"name": "Norfolk Island", "code": "NF"}, 
    {"name": "Northern Mariana Islands", "code": "MP"}, 
    {"name": "Norway", "code": "NO"}, 
    {"name": "Oman", "code": "OM"}, 
    {"name": "Pakistan", "code": "PK"}, 
    {"name": "Palau", "code": "PW"}, 
    {"name": "Palestinian Territory, Occupied", "code": "PS"}, 
    {"name": "Panama", "code": "PA"}, 
    {"name": "Papua New Guinea", "code": "PG"}, 
    {"name": "Paraguay", "code": "PY"}, 
    {"name": "Peru", "code": "PE"}, 
    {"name": "Philippines", "code": "PH"}, 
    {"name": "Pitcairn", "code": "PN"}, 
    {"name": "Poland", "code": "PL"}, 
    {"name": "Portugal", "code": "PT"}, 
    {"name": "Puerto Rico", "code": "PR"}, 
    {"name": "Qatar", "code": "QA"}, 
    {"name": "Reunion", "code": "RE"}, 
    {"name": "Romania", "code": "RO"}, 
    {"name": "Russian Federation", "code": "RU"}, 
    {"name": "RWANDA", "code": "RW"}, 
    {"name": "Saint Helena", "code": "SH"}, 
    {"name": "Saint Kitts and Nevis", "code": "KN"}, 
    {"name": "Saint Lucia", "code": "LC"}, 
    {"name": "Saint Pierre and Miquelon", "code": "PM"}, 
    {"name": "Saint Vincent and the Grenadines", "code": "VC"}, 
    {"name": "Samoa", "code": "WS"}, 
    {"name": "San Marino", "code": "SM"}, 
    {"name": "Sao Tome and Principe", "code": "ST"}, 
    {"name": "Saudi Arabia", "code": "SA"}, 
    {"name": "Senegal", "code": "SN"}, 
    {"name": "Serbia", "code": "RS"}, 
    {"name": "Seychelles", "code": "SC"}, 
    {"name": "Sierra Leone", "code": "SL"}, 
    {"name": "Singapore", "code": "SG"}, 
    {"name": "Slovakia", "code": "SK"}, 
    {"name": "Slovenia", "code": "SI"}, 
    {"name": "Solomon Islands", "code": "SB"}, 
    {"name": "Somalia", "code": "SO"}, 
    {"name": "South Africa", "code": "ZA"}, 
    {"name": "South Georgia and the South Sandwich Islands", "code": "GS"}, 
    {"name": "Spain", "code": "ES"}, 
    {"name": "Sri Lanka", "code": "LK"}, 
    {"name": "Sudan", "code": "SD"}, 
    {"name": "Suriname", "code": "SR"}, 
    {"name": "Svalbard and Jan Mayen", "code": "SJ"}, 
    {"name": "Swaziland", "code": "SZ"}, 
    {"name": "Sweden", "code": "SE"}, 
    {"name": "Switzerland", "code": "CH"}, 
    {"name": "Syrian Arab Republic", "code": "SY"}, 
    {"name": "Taiwan, Province of China", "code": "TW"}, 
    {"name": "Tajikistan", "code": "TJ"}, 
    {"name": "Tanzania, United Republic of", "code": "TZ"}, 
    {"name": "Thailand", "code": "TH"}, 
    {"name": "Timor-Leste", "code": "TL"}, 
    {"name": "Togo", "code": "TG"}, 
    {"name": "Tokelau", "code": "TK"}, 
    {"name": "Tonga", "code": "TO"}, 
    {"name": "Trinidad and Tobago", "code": "TT"}, 
    {"name": "Tunisia", "code": "TN"}, 
    {"name": "Turkey", "code": "TR"}, 
    {"name": "Turkmenistan", "code": "TM"}, 
    {"name": "Turks and Caicos Islands", "code": "TC"}, 
    {"name": "Tuvalu", "code": "TV"}, 
    {"name": "Uganda", "code": "UG"}, 
    {"name": "Ukraine", "code": "UA"}, 
    {"name": "United Arab Emirates", "code": "AE"}, 
    {"name": "United Kingdom", "code": "GB"}, 
    {"name": "United States", "code": "US"}, 
    {"name": "United States Minor Outlying Islands", "code": "UM"}, 
    {"name": "Uruguay", "code": "UY"}, 
    {"name": "Uzbekistan", "code": "UZ"}, 
    {"name": "Vanuatu", "code": "VU"}, 
    {"name": "Venezuela", "code": "VE"}, 
    {"name": "Viet Nam", "code": "VN"}, 
    {"name": "Virgin Islands, British", "code": "VG"}, 
    {"name": "Virgin Islands, U.S.", "code": "VI"}, 
    {"name": "Wallis and Futuna", "code": "WF"}, 
    {"name": "Western Sahara", "code": "EH"}, 
    {"name": "Yemen", "code": "YE"}, 
    {"name": "Zambia", "code": "ZM"}, 
    {"name": "Zimbabwe", "code": "ZW"}
] as const

export type License_Origin = typeof AllCountries[number]['code']

export type res_get_me = {
    id: string,
    name: string,
    username: string,
    avatar: string,
    manages: string[],
    teams: string[],
    admin: boolean,
}